(() => {
  const getDrawer = () => document.querySelector('cart-drawer');
  const getCartLink = () => document.querySelector('#cart-icon-bubble');
  const cartPath = (window.routes && window.routes.cart_url) || '/cart';
  const cartAddUrl = () => (window.routes && window.routes.cart_add_url) || '/cart/add.js';

  const forceOpenDrawer = () => {
    const drawer = getDrawer();
    if (!drawer) return false;

    if (typeof drawer.open === 'function') {
      drawer.open(getCartLink() || undefined);
    } else {
      drawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    }

    return true;
  };

  const openDrawer = () => forceOpenDrawer();

  const fetchFreshCartSections = async () => {
    const url = `${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`;
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) return null;
    return response.json();
  };

  const renderCartSections = async (sections) => {
    const drawer = getDrawer();

    if (!sections || !sections['cart-drawer']) {
      sections = await fetchFreshCartSections();
    }

    if (drawer && sections && sections['cart-drawer'] && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents({ sections });
        window.setTimeout(forceOpenDrawer, 80);
        return;
      } catch (error) {
        console.error(error);
      }
    }

    if (sections && sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const source = html.querySelector('.shopify-section');
      if (bubble && source) bubble.innerHTML = source.innerHTML;
    }

    forceOpenDrawer();
  };

  const addVariant = async ({ id, quantity = 1, properties = {} }) => {
    if (!id) return;

    const body = JSON.stringify({
      id: Number(id),
      quantity: Math.max(1, Number(quantity) || 1),
      properties,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body,
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Unable to add this product to cart.';
      throw new Error(message);
    }

    await renderCartSections(data.sections);
    document.dispatchEvent(new CustomEvent('premium:cart:added', { detail: data }));
    return data;
  };

  const addForm = async (form) => {
    const formData = new FormData(form);
    if (!formData.get('id')) return;

    formData.append('sections', ['cart-drawer', 'cart-icon-bubble']);
    formData.append('sections_url', window.location.pathname);

    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Unable to add this product to cart.';
      throw new Error(message);
    }

    await renderCartSections(data.sections);
    document.dispatchEvent(new CustomEvent('premium:cart:added', { detail: data }));
    return data;
  };

  const getProductAddForm = (target) => {
    if (!target || !target.matches) return null;
    const form = target.closest ? target.closest('form') : target;
    if (!form || !form.matches) return null;

    const isProductAddForm =
      form.matches('form[action*="/cart/add"]') ||
      form.matches('form[data-type="add-to-cart-form"]') ||
      form.hasAttribute('data-premium-ajax-cart');

    if (!isProductAddForm || !form.querySelector('[name="id"]')) return null;
    return form;
  };

  const setButtonLoading = (button, isLoading) => {
    if (!button) return;
    button.toggleAttribute('aria-busy', isLoading);
    button.classList.toggle('loading', isLoading);
    if (isLoading) {
      button.setAttribute('data-premium-was-disabled', button.disabled ? 'true' : 'false');
      button.disabled = true;
    } else {
      const wasDisabled = button.getAttribute('data-premium-was-disabled') === 'true';
      button.removeAttribute('data-premium-was-disabled');
      if (!wasDisabled) button.disabled = false;
    }

    const spinner = button.querySelector('.loading__spinner') || button.closest('product-form')?.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  };

  document.addEventListener(
    'submit',
    async (event) => {
      const form = getProductAddForm(event.target);
      if (!form || form.dataset.premiumAjaxSubmitting === 'true') return;

      const submitter = event.submitter || form.querySelector('button[name="add"], [type="submit"]');
      if (submitter && submitter.name && submitter.name !== 'add') return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      form.dataset.premiumAjaxSubmitting = 'true';
      setButtonLoading(submitter, true);

      try {
        await addForm(form);
      } catch (error) {
        console.error(error);
        alert(error.message || 'Unable to add this product to cart.');
      } finally {
        delete form.dataset.premiumAjaxSubmitting;
        setButtonLoading(submitter, false);
      }
    },
    true
  );

  document.addEventListener('click', async (event) => {
    const cartLink = event.target.closest('a[href]');
    if (cartLink && !cartLink.hasAttribute('data-premium-view-cart')) {
      const linkUrl = new URL(cartLink.href, window.location.origin);
      const currentPath = window.location.pathname.replace(/\/$/, '');
      const cartPathClean = cartPath.replace(/\/$/, '');

      if (linkUrl.origin === window.location.origin && linkUrl.pathname.replace(/\/$/, '') === cartPathClean && currentPath !== cartPathClean) {
        const drawer = getDrawer();
        if (drawer) {
          event.preventDefault();
          forceOpenDrawer();
          return;
        }
      }
    }

    const button = event.target.closest('[data-premium-add-to-cart], [data-add-to-cart], .custom-add-to-cart');
    if (!button) return;

    const variantId = button.dataset.variantId || button.getAttribute('data-product-variant-id');
    if (!variantId) return;

    event.preventDefault();

    const originalText = button.textContent;
    setButtonLoading(button, true);

    try {
      await addVariant({
        id: variantId,
        quantity: button.dataset.quantity || 1,
      });
    } catch (error) {
      console.error(error);
      alert(error.message || 'Unable to add this product to cart.');
    } finally {
      setButtonLoading(button, false);
      if (originalText) button.textContent = originalText;
    }
  });

  window.PremiumCart = {
    add: addVariant,
    addForm,
    open: forceOpenDrawer,
    render: renderCartSections,
  };
})();
